"use client";

import { ModelInfo, MODEL_COLORS, OUR_MODELS, BASELINE_MODELS } from "@/lib/types";

export type ViewMode = "overview" | "wer-analysis" | "baselines";

interface SidebarProps {
  models: ModelInfo[];
  selectedModel: string | null;
  selectedCheckpoint: string | null;
  activeView: ViewMode;
  onSelectModel: (id: string) => void;
  onSelectCheckpoint: (ckpt: string) => void;
  onViewChange: (view: ViewMode) => void;
}

export default function Sidebar({
  models,
  selectedModel,
  selectedCheckpoint,
  activeView,
  onSelectModel,
  onSelectCheckpoint,
  onViewChange,
}: SidebarProps) {
  const ourModels = models.filter((m) => (OUR_MODELS as readonly string[]).includes(m.id));
  const baselineModels = models.filter((m) => (BASELINE_MODELS as readonly string[]).includes(m.id));

  const navItems: { key: ViewMode; label: string }[] = [
    { key: "overview", label: "Overview & Plots" },
    { key: "wer-analysis", label: "WER Analysis" },
    { key: "baselines", label: "vs Baselines" },
  ];

  return (
    <aside className="w-64 border-r border-[var(--color-border)] bg-[var(--color-background)] flex flex-col h-full overflow-y-auto">
      {/* Logo */}
      <div className="p-5 border-b border-[var(--color-border)]">
        <h1 className="text-sm font-bold tracking-wider uppercase text-[var(--color-foreground)]">
          ASR Benchmark
        </h1>
        <p className="text-[10px] text-[var(--color-muted)] mt-1 tracking-wide">
          Indic Language Evaluation
        </p>
      </div>

      {/* Navigation */}
      <div className="p-4 border-b border-[var(--color-border)]">
        <p className="text-[10px] uppercase tracking-widest text-[var(--color-muted)] mb-3">
          Navigation
        </p>
        {navItems.map((item) => (
          <button
            key={item.key}
            onClick={() => onViewChange(item.key)}
            className={`w-full text-left px-3 py-2 rounded text-xs transition-colors mt-0.5 ${
              activeView === item.key
                ? "bg-[var(--color-card-hover)] text-[var(--color-foreground)]"
                : "text-[var(--color-muted)] hover:text-[var(--color-foreground)] hover:bg-[var(--color-card)]"
            }`}
          >
            {item.label}
          </button>
        ))}
      </div>

      {/* Our Models */}
      <div className="p-4 border-b border-[var(--color-border)]">
        <p className="text-[10px] uppercase tracking-widest text-[var(--color-muted)] mb-3">
          Our Models
        </p>
        {ourModels.map((model) => (
          <ModelEntry
            key={model.id}
            model={model}
            selectedModel={selectedModel}
            selectedCheckpoint={selectedCheckpoint}
            onSelectModel={onSelectModel}
            onSelectCheckpoint={onSelectCheckpoint}
          />
        ))}
      </div>

      {/* Baseline Models */}
      {baselineModels.length > 0 && (
        <div className="p-4 flex-1">
          <p className="text-[10px] uppercase tracking-widest text-[var(--color-muted)] mb-3">
            API Baselines
          </p>
          {baselineModels.map((model) => (
            <ModelEntry
              key={model.id}
              model={model}
              selectedModel={selectedModel}
              selectedCheckpoint={selectedCheckpoint}
              onSelectModel={onSelectModel}
              onSelectCheckpoint={onSelectCheckpoint}
            />
          ))}
        </div>
      )}

      {/* Footer */}
      <div className="p-4 border-t border-[var(--color-border)]">
        <p className="text-[10px] text-[var(--color-muted)]">
          Dataset: indic-asr-benchmark-6k
        </p>
        <p className="text-[10px] text-[var(--color-muted)]">
          12 languages &middot; 6,000 samples
        </p>
      </div>
    </aside>
  );
}

function ModelEntry({
  model, selectedModel, selectedCheckpoint, onSelectModel, onSelectCheckpoint,
}: {
  model: ModelInfo;
  selectedModel: string | null;
  selectedCheckpoint: string | null;
  onSelectModel: (id: string) => void;
  onSelectCheckpoint: (ckpt: string) => void;
}) {
  return (
    <div className="mb-2">
      <button
        onClick={() => {
          onSelectModel(model.id);
          if (model.checkpoints.length > 0) {
            onSelectCheckpoint(model.checkpoints[model.checkpoints.length - 1]);
          }
        }}
        className={`w-full text-left px-3 py-1.5 rounded text-[11px] transition-colors flex items-center gap-2 ${
          selectedModel === model.id
            ? "bg-[var(--color-card-hover)] text-[var(--color-foreground)]"
            : "text-[var(--color-muted)] hover:text-[var(--color-foreground)] hover:bg-[var(--color-card)]"
        }`}
      >
        <span
          className="w-2 h-2 rounded-full flex-shrink-0"
          style={{ backgroundColor: MODEL_COLORS[model.id] || "#6366f1" }}
        />
        <span className="truncate">{model.displayName}</span>
      </button>

      {selectedModel === model.id && model.checkpoints.length > 1 && (
        <div className="ml-6 mt-0.5 space-y-0.5">
          {model.checkpoints.map((ckpt) => (
            <button
              key={ckpt}
              onClick={() => onSelectCheckpoint(ckpt)}
              className={`w-full text-left px-3 py-1 rounded text-[10px] transition-colors ${
                selectedCheckpoint === ckpt
                  ? "text-[var(--color-foreground)] bg-[var(--color-card)]"
                  : "text-[var(--color-muted)] hover:text-[var(--color-accent)]"
              }`}
            >
              {ckpt}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
